import { useState, useCallback } from "react";
import type { DynamicSort, SortDirection } from "../types";
import type { SortableAttribute } from "../types";

export interface SortBuilderState {
  sorts: DynamicSort[];
  addSort: (attribute: SortableAttribute) => void;
  updateSort: (index: number, updates: Partial<DynamicSort>) => void;
  removeSort: (index: number) => void;
  clearSorts: () => void;
  setSorts: (sorts: DynamicSort[]) => void;
}

export function useSortBuilder(
  initialSorts: DynamicSort[] = []
): SortBuilderState {
  const [sorts, setSorts] = useState<DynamicSort[]>(initialSorts);

  const addSort = useCallback((attribute: SortableAttribute) => {
    const newSort: DynamicSort = {
      field: attribute.field,
      direction: 0 as SortDirection, // Default to ASC
      nestedField: attribute.nestedField,
    };
    setSorts((prev) => [...prev, newSort]);
  }, []);

  const updateSort = useCallback(
    (index: number, updates: Partial<DynamicSort>) => {
      setSorts((prev) =>
        prev.map((sort, i) => (i === index ? { ...sort, ...updates } : sort))
      );
    },
    []
  );

  const removeSort = useCallback((index: number) => {
    setSorts((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearSorts = useCallback(() => {
    setSorts([]);
  }, []);

  return {
    sorts,
    addSort,
    updateSort,
    removeSort,
    clearSorts,
    setSorts,
  };
}
